import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  getVisibleCharacters,
  getUserDashboardSummary,
  type DbCharacter,
  type UserDashboardSummary,
} from '@/api/astraea';
import { getCredits } from '@/api/auth';
import { DAILY_PACK_RECOMMENDATIONS } from '@/lib/materialScenes';

function StatCard({ icon, label, value, tone }: { icon: string; label: string; value: number | string; tone: string }) {
  return (
    <div className="rounded-xl border border-secondary-200 bg-background-50 p-3.5">
      <div className={`inline-flex items-center justify-center w-8 h-8 rounded-lg mb-2 ${tone}`}><i className={icon}></i></div>
      <div className="text-xl font-bold text-foreground-950 leading-none">{value}</div>
      <div className="text-[11px] text-secondary-500 mt-1">{label}</div>
    </div>
  );
}

export default function WorkbenchPage() {
  const nav = useNavigate();
  const [chars, setChars] = useState<DbCharacter[]>([]);
  const [summary, setSummary] = useState<UserDashboardSummary | null>(null);
  const [credits, setCredits] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const cs = await getVisibleCharacters();
      setChars(cs);
      const dash = await getUserDashboardSummary();
      setSummary(dash);
      getCredits().then(setCredits).catch(() => {});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const primaryCharId = chars[0]?.id;
  const packHref = primaryCharId ? `/materials/${primaryCharId}` : '/materials';

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-6 py-8 md:py-12">
      <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">今天开始经营</h1>
      <p className="mt-2 text-sm text-foreground-600 leading-relaxed max-w-2xl">
        选角色后，AI 直接给出今日建议与素材清单。你勾选、确认点数，系统帮你生成，同页下载就能用。
      </p>

      {/* 今日主入口 */}
      <section className="mt-6 rounded-2xl border border-primary-200 bg-primary-50/50 p-5 md:p-6">
        <h2 className="text-base font-semibold text-foreground-950"><i className="ri-flashlight-line text-primary-500 mr-1"></i>一键进入今日工作</h2>
        <ol className="mt-3 space-y-2 text-sm text-foreground-700">
          {DAILY_PACK_RECOMMENDATIONS.map((r) => (
            <li key={r.n} className="flex gap-2">
              <span className="text-primary-600 font-semibold w-5">{r.n}.</span>
              <span><span className="font-medium">{r.title}</span><span className="text-secondary-500"> — 用途：{r.purpose}</span></span>
            </li>
          ))}
        </ol>
        <div className="flex flex-wrap gap-2 mt-5">
          <Link to={packHref} className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 cursor-pointer">
            <i className="ri-play-circle-line"></i>开始今日经营
          </Link>
          <Link to={primaryCharId ? `/materials/${primaryCharId}` : '/materials'} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium border border-primary-200 text-primary-700 bg-white cursor-pointer">
            进入角色素材库
          </Link>
          <Link to="/create" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium border border-secondary-200 text-foreground-600 cursor-pointer">
            新建角色
          </Link>
        </div>
      </section>

      {/* 双模式入口 */}
      <div className="grid sm:grid-cols-2 gap-4 mt-8">
        <Link to="/materials" className="group rounded-2xl border-2 border-primary-300 bg-primary-50/50 p-6 hover:border-primary-400 hover:shadow-md transition-all cursor-pointer">
          <div className="w-12 h-12 rounded-xl bg-primary-500 text-white flex items-center justify-center text-2xl mb-4"><i className="ri-image-circle-line"></i></div>
          <h2 className="text-lg font-bold text-foreground-950">直接拿素材去发 / 去聊</h2>
          <p className="text-sm text-foreground-600 mt-2 leading-relaxed">角色生活照、无声影片、破冰话术、引流钩子。业务素材模式，不讲策略。</p>
          <span className="inline-flex items-center gap-1 text-sm font-semibold text-primary-600 mt-4 group-hover:gap-2 transition-all">进入素材库 <i className="ri-arrow-right-line"></i></span>
        </Link>
        <Link to="/board" className="group rounded-2xl border border-secondary-200 bg-background-50 p-6 hover:border-secondary-300 hover:shadow-sm transition-all cursor-pointer">
          <div className="w-12 h-12 rounded-xl bg-secondary-100 text-secondary-600 flex items-center justify-center text-2xl mb-4"><i className="ri-compass-3-line"></i></div>
          <h2 className="text-lg font-bold text-foreground-950">经营一个角色帐号</h2>
          <p className="text-sm text-foreground-600 mt-2 leading-relaxed">本周任务、平台分工、私域承接、数据回填。给长期做 IP 的人。</p>
          <span className="inline-flex items-center gap-1 text-sm font-medium text-secondary-600 mt-4 group-hover:gap-2 transition-all">角色经营模式 <i className="ri-arrow-right-line"></i></span>
        </Link>
      </div>
      <section className="mt-4 rounded-2xl border border-secondary-200 bg-background-50 p-5 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-accent-50 text-accent-600 flex items-center justify-center text-2xl shrink-0">
          <i className="ri-movie-2-line"></i>
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-bold text-foreground-950">角色视频工作区</h2>
          <p className="text-sm text-foreground-600 mt-1 leading-relaxed">单场景、分镜、翻译、声音和项目草稿集中处理。</p>
        </div>
        <Link to="/role-video" className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-accent-600 text-white cursor-pointer">
          进入视频<i className="ri-arrow-right-line"></i>
        </Link>
      </section>

      {loading ? (
        <div className="py-16 text-center text-sm text-secondary-400"><div className="inline-block w-6 h-6 border-2 border-secondary-200 border-t-primary-500 rounded-full animate-spin mb-3"></div><div>读取中…</div></div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mt-8">
            <StatCard icon="ri-user-star-line" label="角色数" value={summary?.total_roles ?? 0} tone="bg-primary-100 text-primary-600" />
            <StatCard icon="ri-image-2-line" label="素材库" value="可用" tone="bg-accent-50 text-accent-600" />
            <StatCard icon="ri-copper-coin-line" label="剩余点数" value={credits === null ? '…' : credits} tone="bg-green-50 text-green-600" />
            <StatCard icon="ri-list-check-2" label="本周任务" value={summary?.weekly_tasks_total ?? 0} tone="bg-secondary-100 text-secondary-600" />
          </div>

          <div className="flex flex-wrap items-center gap-2 mt-5">
            <Link to="/create" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 cursor-pointer">
              <i className="ri-add-line"></i>新建角色
            </Link>
            <Link to="/materials" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border border-primary-200 text-primary-700 bg-primary-50 cursor-pointer"><i className="ri-gallery-line"></i>角色素材库</Link>
            <Link to="/reply" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border border-secondary-200 text-foreground-600 cursor-pointer"><i className="ri-chat-smile-2-line"></i>回复优化</Link>
          </div>

          {chars.length === 0 ? (
            <div className="mt-8 rounded-2xl border border-dashed border-secondary-300 bg-background-50 p-8 text-center">
              <i className="ri-user-smile-line text-3xl text-primary-400"></i>
              <div className="text-base font-semibold text-foreground-900 mt-2">先建一个角色</div>
              <p className="text-sm text-foreground-600 mt-2 max-w-md mx-auto">固定五官、声音和生活感，然后一键出照片、无声影片和破冰话术。</p>
              <Link to="/create" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-primary-500 text-background-50 mt-4 cursor-pointer"><i className="ri-add-line"></i>一键新建角色</Link>
            </div>
          ) : (
            <section className="mt-8">
              <h2 className="text-base font-semibold text-foreground-950 mb-3"><i className="ri-gallery-line text-primary-500 mr-1"></i>今日可用素材 · 选角色</h2>
              <div className="space-y-2">
                {chars.map((c) => (
                  <div key={c.id} className="rounded-xl border border-secondary-200 bg-background-50 p-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-primary-100 flex-shrink-0">
                      {c.avatar_url ? <img src={c.avatar_url} alt={c.name} className="w-full h-full object-cover" /> : <i className="ri-user-smile-line text-primary-600 flex items-center justify-center h-full"></i>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-foreground-950">{c.name}</div>
                      <div className="text-[11px] text-secondary-500 truncate">{c.description}</div>
                    </div>
                    <Link to={`/materials/${c.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-primary-500 text-white flex-shrink-0 cursor-pointer">
                      拿素材<i className="ri-arrow-right-line"></i>
                    </Link>
                    <button type="button" onClick={() => nav(`/board/${c.id}`)} className="text-[11px] text-secondary-400 hover:text-secondary-600 flex-shrink-0 cursor-pointer">经营</button>
                  </div>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
